// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * A footer component for downloading data exports with loading state and progress estimation.
 * Displays a clickable link that triggers an async download operation.
 * When itemCount is provided, shows estimated time remaining during the export.
 */
import * as Sentry from '@sentry/react';
import { api } from '@/api/axios-instance';
import { FooterContainer } from '@/components/footer-container';
import { DownloadRounded } from '@/constants/hardfin-standard-icons';
import { getFilenameFromContentDisposition } from '@/file/utilities/get-filename-from-content-disposition';
import { formatNumber } from '@/utilities/number';
import { CircularProgress, Skeleton, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';

// Default processing rate for exports (items per second)
const DEFAULT_ITEMS_PER_SECOND = 4400;

interface Props {
  /**
   * The URL to download from. The pathname and search params will be used
   * to construct the API request.
   */
  downloadURL: URL;

  /**
   * The label to display on the download link.
   * Should use sentence case (e.g., "Export asset data").
   */
  downloadLabel: string;

  /**
   * Optional total number of items to be exported.
   * - `number`: enables progress estimation during the export.
   * - `null`: signals that the consuming hook has not finished its first
   *   fetch yet. The footer renders a skeleton in this state so callers
   *   don't have to wrap it themselves to avoid an "Export 0 items" flash.
   * - `undefined`: no count is available (skips the time estimate but
   *   keeps the footer interactive — matches the historical behaviour).
   */
  itemCount?: number | null;

  /**
   * Optional processing rate in items per second.
   * Used to calculate estimated time remaining.
   * Defaults to 4,400 items per second.
   */
  itemsPerSecond?: number;
}

/**
 * Formats the estimated time remaining for the export operation.
 * Returns a human-readable string like "~2 seconds" or "~1 minute".
 *
 * @param seconds - The estimated number of seconds remaining
 * @returns A formatted string representing the estimated time
 */
function formatEstimatedTime(seconds: number): string {
  if (seconds < 1) {
    return '~1 sec';
  }
  if (seconds < 60) {
    const roundedSeconds = Math.ceil(seconds);
    return `~${roundedSeconds} sec`;
  }
  const minutes = seconds / 60;
  return `~${formatNumber(minutes, { digits: 1 })} min`;
}

/**
 * Generates a filename for the export based on the download label.
 * Converts the label to a slug format and appends a timestamp.
 *
 * @param label - The download label to use as the base filename
 * @returns A filename string with timestamp and .csv extension
 */
function generateFilename(label: string): string {
  const slug = label.toLowerCase().replace(/\s+/g, '-');
  const timestamp = new Date()
    .toLocaleString('sv')
    .replace(/:/g, '-')
    .replace(' ', '_');
  return `${slug}_${timestamp}.csv`;
}

/**
 * A footer component for downloading data exports with loading state and progress estimation.
 * When clicked, triggers an async download operation and displays:
 * - A disabled state during export
 * - A spinner animation
 * - An estimated time remaining (when itemCount is provided)
 *
 * @param props - Component props for configuring the download behavior
 */
// Width of the skeleton placeholder — sized to roughly match a populated
// "Export charge data" link so the layout doesn't shift when real data
// arrives.
const SKELETON_WIDTH_PX = 180;
const SKELETON_HEIGHT_PX = 20;

export function DownloadFooter(props: Props) {
  const [isPending, setIsPending] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  const itemsPerSecond = props.itemsPerSecond ?? DEFAULT_ITEMS_PER_SECOND;

  // Calculate estimated time remaining based on item count
  const estimatedTotalSeconds = props.itemCount
    ? props.itemCount / itemsPerSecond
    : 0;
  const estimatedRemainingSeconds = Math.max(
    0,
    estimatedTotalSeconds - elapsedSeconds
  );

  // Track elapsed time during export
  useEffect(() => {
    if (!isPending) {
      setElapsedSeconds(0);
      return;
    }

    const interval = setInterval(() => {
      setElapsedSeconds((prev) => prev + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [isPending]);

  // Cold-load skeleton: render a placeholder while the consuming hook is
  // still fetching. Lets pages pass the listing's `totalCount` straight
  // through without conditionally rendering the footer themselves. Kept
  // after the hook calls so the rules-of-hooks order stays stable.
  if (props.itemCount === null) {
    return (
      <FooterContainer variant="outlined">
        <Skeleton
          variant="rounded"
          width={SKELETON_WIDTH_PX}
          height={SKELETON_HEIGHT_PX}
        />
      </FooterContainer>
    );
  }

  const handleClick = async () => {
    if (isPending) {
      return;
    }

    setIsPending(true);

    try {
      const path = props.downloadURL.pathname + props.downloadURL.search;
      const response = await api.get(path, {
        responseType: 'blob',
      });

      // Validate that we got a blob response
      if (!response.data || !(response.data instanceof Blob)) {
        throw new Error(
          'Invalid response format received from export endpoint'
        );
      }

      // Get filename from Content-Disposition header, fall back to generated name
      const contentDisposition = response.headers['content-disposition'];
      const filename =
        getFilenameFromContentDisposition(contentDisposition) ??
        generateFilename(props.downloadLabel);

      // Create a download link for the file
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      // Capture the error with Sentry for tracking
      Sentry.captureException(error);
    } finally {
      setIsPending(false);
    }
  };

  return (
    <FooterContainer variant="outlined">
      <Typography
        variant="subtitle2"
        component="button"
        onClick={handleClick}
        sx={{
          cursor: isPending ? 'default' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          background: 'none',
          border: 'none',
          padding: 0,
          fontFamily: 'inherit',
          color: isPending ? 'text.disabled' : 'primary.main',
          '&:hover': {
            textDecoration: isPending ? 'none' : 'underline',
          },
        }}
        aria-disabled={isPending}
      >
        {isPending ? (
          <Stack direction="row" alignItems="center" spacing={1}>
            <CircularProgress size={14} />
            <span>
              Exporting...
              {props.itemCount
                ? ` (${formatEstimatedTime(estimatedRemainingSeconds)} remaining)`
                : ''}
            </span>
          </Stack>
        ) : (
          <>
            {props.downloadLabel}&nbsp;
            <DownloadRounded fontSize="inherit" color="primary" />
          </>
        )}
      </Typography>
    </FooterContainer>
  );
}
